import { createEntityAdapter } from '@reduxjs/toolkit';
import * as R from 'ramda';

import {
  fetchEpisodeLearningPathsRequest,
  fetchLearningPathRequest,
  fetchLearningPathsRequest,
  fetchOrgPinnedLpsRequest,
  pinLpRequest,
  reorderFeaturedLpsRequest,
  unpinLpRequest,
} from '@/requests';
import { ComplianceType, LearningPath } from '@/types';
import { camelCaseKeys } from '@/utils';

import { postgrestApi } from '../postgrest-api';
import { PostgrestApiTags } from '../store.constants';
import { rtkQueryError } from '../store.utils';
import { transformLearningPath } from './learning-paths.utils';

export const learningPathsAdapter = createEntityAdapter({
  selectId: (learningPath: LearningPath) => learningPath.groupId,
});

export const learningPathsApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchLearningPaths: builder.query<
      LearningPath[],
      { compliance: ComplianceType }
    >({
      queryFn: async (params, redux, extraOptions, baseQuery) => {
        try {
          const { data: learningPaths, error: learningPathsError } =
            await baseQuery(fetchLearningPathsRequest(params));

          const { data: orgPinnedLpsData, error: orgPinnedLpsError } =
            await baseQuery(fetchOrgPinnedLpsRequest());

          if (learningPathsError) return { error: learningPathsError };
          if (orgPinnedLpsError) return { error: orgPinnedLpsError };

          const orgPinnedLpsMap = R.pipe(
            R.defaultTo([]),
            R.map(camelCaseKeys),
            R.indexBy(R.prop('groupId')),
          )(orgPinnedLpsData as unknown[]);

          const learningPathsWithOverrides = (learningPaths as unknown[])
            .map(transformLearningPath)
            .map(lp => ({
              ...lp,
              isPinnedByOrg: lp.groupId in orgPinnedLpsMap,
              featuredLearningPathId:
                orgPinnedLpsMap[lp.groupId]?.featuredLearningPathId,
              orderId: orgPinnedLpsMap[lp.groupId]?.orderId,
            }));

          return { data: learningPathsWithOverrides };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
      providesTags: [PostgrestApiTags.LEARNING_PATHS],
    }),
    fetchOrgPinnedLps: builder.query<
      { groupId: number; orderId: number }[],
      void
    >({
      query: fetchOrgPinnedLpsRequest,
      transformResponse: camelCaseKeys,
      providesTags: [PostgrestApiTags.ORG_PINNED_LPS],
    }),
    reorderOrgPinnedLps: builder.mutation<
      void,
      { featuredLearningPathId: number; newOrderId: number }
    >({
      query: reorderFeaturedLpsRequest,
      invalidatesTags: [
        PostgrestApiTags.ORG_PINNED_LPS,
        PostgrestApiTags.LEARNING_PATHS,
      ],
    }),
    pinLp: builder.mutation<void, { groupId: number }>({
      query: pinLpRequest,
      invalidatesTags: [
        PostgrestApiTags.ORG_PINNED_LPS,
        PostgrestApiTags.LEARNING_PATHS,
      ],
    }),
    unpinLp: builder.mutation<void, { groupId: number }>({
      query: unpinLpRequest,
      invalidatesTags: [
        PostgrestApiTags.ORG_PINNED_LPS,
        PostgrestApiTags.LEARNING_PATHS,
      ],
    }),
    fetchLearningPath: builder.query<
      LearningPath | undefined,
      { compliance: ComplianceType; learningPathId: number }
    >({
      query: fetchLearningPathRequest,
      transformResponse: (response: any) =>
        response ? transformLearningPath(response[0]) : undefined,
    }),
    fetchEpisodeLearningPaths: builder.query<
      LearningPath[],
      { episodeId: string; compliance: ComplianceType }
    >({
      queryFn: async (params, redux, extraOptions, baseQuery) => {
        const { episodeId, compliance } = params;

        const { data: episodeLearningPaths, error } = await baseQuery(
          fetchEpisodeLearningPathsRequest({ episodeId }),
        );
        if (error) return { error };

        const learningPathIds = (episodeLearningPaths as any[]).map(
          i => i.group_id,
        );

        const { data: learningPaths, error: learningPathsError } =
          await baseQuery(
            fetchLearningPathsRequest({ compliance, learningPathIds }),
          );
        if (learningPathsError) return { error: learningPathsError };

        const learningPathsWithOverrides = (learningPaths as unknown[]).map(
          transformLearningPath,
        );
        return { data: learningPathsWithOverrides };
      },
    }),
  }),
});

export const {
  useFetchLearningPathsQuery,
  useFetchOrgPinnedLpsQuery,
  useFetchLearningPathQuery,
  useReorderOrgPinnedLpsMutation,
  usePinLpMutation,
  useUnpinLpMutation,
  useFetchEpisodeLearningPathsQuery,
} = learningPathsApi;
